import React, { useState, useEffect } from "react";
import { useParams, useSearchParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Eye, EyeOff, ArrowRight, ArrowLeft, Heart, Check } from "lucide-react";
import WhatsAppFab from "@/components/WhatsAppFab";
import { toast } from "sonner";
import { ANIMALS } from "@/lib/animals";
import { motion } from "framer-motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PLAN_LABELS = { bulan1: "Paket 1 Bulan", bulan6: "Paket 6 Bulan", premium: "Paket Premium" };

export default function Register() {
  const { token } = useParams();
  const [sp] = useSearchParams();
  const preselectedPlan = sp.get("plan") || "";
  const nav = useNavigate();
  const [linkInfo, setLinkInfo] = useState(null); // null | {ok, plan, note} | {invalid: true}
  const [loading, setLoading] = useState(false);
  const [show, setShow] = useState(false);
  const [f, setF] = useState({ email: "", username: "", password: "", password2: "", gender: "Pria", animal: "koala" });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/public/reg-link/${token}`).then((r) => setLinkInfo(r.data)).catch(() => setLinkInfo({ invalid: true }));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.email.trim() || !f.username.trim() || !f.password) { toast.error("Lengkapi semua kolom"); return; }
    if (f.password.length < 4) { toast.error("Password minimal 4 karakter"); return; }
    if (f.password !== f.password2) { toast.error("Konfirmasi password tidak sama"); return; }
    setLoading(true);
    try {
      const payload = {
        email: f.email.trim(), username: f.username.trim(),
        password: f.password, gender: f.gender, animal: f.animal,
        ...(token ? { token } : {}),
        ...(!token && preselectedPlan ? { plan: preselectedPlan } : {}),
      };
      const { data } = await axios.post(`${API}/public/register`, payload);
      if (data.auto_activated) {
        toast.success("Pendaftaran berhasil! Paket sudah aktif. Silakan login.");
        setTimeout(() => nav("/login"), 800);
      } else if (data.plan_status === "pending") {
        toast.success("Pendaftaran berhasil! Silakan login & selesaikan pembayaran untuk mengaktifkan paket.");
        setTimeout(() => nav("/login"), 800);
      } else {
        toast.success("Pendaftaran berhasil! Silakan login.");
        setTimeout(() => nav("/login"), 800);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Pendaftaran gagal");
    } finally { setLoading(false); }
  };

  if (token && !linkInfo) return <div className="flex min-h-screen items-center justify-center bg-[#0a0a0c] text-white/70">Memuat link…</div>;
  if (token && linkInfo.invalid) return (
    <div className="flex min-h-screen items-center justify-center bg-[#0a0a0c] px-6 text-center text-white">
      <div>
        <p className="font-heading text-3xl">Link Tidak Valid</p>
        <p className="mt-2 text-sm text-white/60">Link pendaftaran sudah digunakan atau kedaluwarsa. Silakan minta link baru ke admin atau daftar sendiri.</p>
        <Link to="/daftar"><Button className="mt-4 rounded-full">Daftar Sendiri</Button></Link>
      </div>
    </div>
  );

  const activePlan = token ? (linkInfo?.plan || "premium") : preselectedPlan;

  return (
    <div className="min-h-screen bg-[#0a0a0c] px-4 py-12 text-white" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
      <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Great+Vibes&family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,400&family=Inter:wght@400;500;600;700&display=swap" />
      <div className="mx-auto max-w-lg">
        <Link to="/" data-testid="register-back-home" className="inline-flex items-center gap-1.5 rounded-full border border-[#c9a35c33] px-3 py-1.5 text-xs text-neutral-300 transition-colors hover:border-[#c9a35c] hover:text-[#e6c98a]">
          <ArrowLeft className="h-3.5 w-3.5" /> Kembali ke Beranda
        </Link>
        <div className="mb-8 mt-6 text-center">
          <img src="/wedifylab_logo3.png" alt="Wedifylab" className="mx-auto h-14 w-14 object-contain drop-shadow-[0_0_18px_#c9a35c66]" />
          <h1 className="mt-4 text-5xl italic" style={{ fontFamily: "'Great Vibes', cursive", color: "#e6c98a" }}>Daftar Akun</h1>
          <p className="mt-2 text-sm text-white/60" style={{ fontFamily: "'Inter', sans-serif" }}>Wedifylab · Wedding Planner &amp; Invitation</p>
          {activePlan && (
            <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-[#c9a35c66] bg-[#c9a35c11] px-4 py-1.5">
              <Check className="h-3.5 w-3.5 text-[#e6c98a]" />
              <span className="text-xs uppercase tracking-[0.2em] text-[#e6c98a]" style={{ fontFamily: "'Inter', sans-serif" }}>
                {token ? "Paket akan dibayar: " : "Paket dipilih: "} <b>{PLAN_LABELS[activePlan] || activePlan}</b>
              </span>
            </div>
          )}
          {token && (
            <p className="mt-3 text-xs text-white/60" style={{ fontFamily: "'Inter', sans-serif" }}>
              Setelah daftar, login &amp; selesaikan pembayaran via Midtrans untuk mengaktifkan paket.
            </p>
          )}
        </div>

        <motion.form onSubmit={submit} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          className="rounded-3xl border border-white/10 bg-white/[0.03] p-8 backdrop-blur-md">
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-white/60">Email</Label>
              <Input data-testid="reg-email" value={f.email} onChange={(e) => set("email", e.target.value)}
                placeholder="email@contoh.com" className="mt-2 border-white/15 bg-white/5" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-white/60">Username</Label>
              <Input data-testid="reg-username" value={f.username} onChange={(e) => set("username", e.target.value)}
                placeholder="username unik" className="mt-2 border-white/15 bg-white/5" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-white/60">Password</Label>
                <div className="relative mt-2">
                  <Input data-testid="reg-password" type={show ? "text" : "password"} value={f.password} onChange={(e) => set("password", e.target.value)}
                    className="border-white/15 bg-white/5 pr-9" />
                  <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 text-white/50">
                    {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-white/60">Ulangi Password</Label>
                <Input data-testid="reg-password2" type={show ? "text" : "password"} value={f.password2} onChange={(e) => set("password2", e.target.value)}
                  className="mt-2 border-white/15 bg-white/5" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-white/60">Gender</Label>
                <select data-testid="reg-gender" value={f.gender} onChange={(e) => set("gender", e.target.value)}
                  className="mt-2 w-full rounded-md border border-white/15 bg-white/5 px-3 py-2 text-sm">
                  <option value="Pria">Pria</option>
                  <option value="Wanita">Wanita</option>
                </select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-white/60">Karakter Hewan</Label>
                <select data-testid="reg-animal" value={f.animal} onChange={(e) => set("animal", e.target.value)}
                  className="mt-2 w-full rounded-md border border-white/15 bg-white/5 px-3 py-2 text-sm">
                  {ANIMALS.map((a) => <option key={a.key} value={a.key}>{a.emoji} {a.name}</option>)}
                </select>
              </div>
            </div>
          </div>
          <Button data-testid="reg-submit" type="submit" disabled={loading}
            className="mt-6 w-full rounded-full py-6 text-[#0f0a08]" style={{ background: "linear-gradient(90deg,#c9a35c,#e6c98a)", fontFamily: "'Inter', sans-serif" }}>
            {loading ? "Memproses..." : <span className="flex items-center justify-center gap-2 font-semibold">Daftar <ArrowRight className="h-4 w-4" /></span>}
          </Button>
          <p className="mt-4 text-center text-xs text-white/40" style={{ fontFamily: "'Inter', sans-serif" }}>
            Sudah punya akun? <Link to="/login" className="text-[#e6c98a] underline">Masuk</Link>
          </p>
        </motion.form>
        <p className="mt-6 flex items-center justify-center gap-2 text-xs text-white/40" style={{ fontFamily: "'Inter', sans-serif" }}>
          <Heart className="h-3 w-3 fill-[#c9a35c] text-[#c9a35c]" /> Wedifylab
        </p>
      </div>
      <WhatsAppFab position="right" context="pendaftaran akun" />
    </div>
  );
}
